/* ============================================================
 * ADG Category Layout — picks the cards-per-row, continuously.
 *
 * WHY THIS EXISTS: Liquid knows how many cards are in a group, CSS knows
 * how wide the container is, and neither knows both. Pure CSS could hold
 * a legibility floor OR divide the row evenly, but not both — which is
 * how you end up with one card marooned on the last row next to a hole.
 *
 * This measures the real width and picks n so that:
 *   1. every row is FULL — no gap-toothed last row, ever
 *   2. a card never shrinks past the legibility floor (--adg-cat-min),
 *      except by a small tolerance when bending it avoids an orphan row,
 *      because slightly-smaller-but-even beats a hole
 *   3. it never exceeds the merchant's Max columns
 *
 * Runs on a ResizeObserver, so dragging the window reflows smoothly and
 * lands on the largest tiles that fit. No breakpoints anywhere.
 *
 * PROGRESSIVE ENHANCEMENT: without JS the CSS fallback still renders a
 * sensible grid — this only sharpens it.
 * ============================================================ */
(function () {
  'use strict';

  var TOLERANCE = 0.85;   // how far below the floor we'll bend to avoid an orphan
  var HARD_MIN  = 0.78;   // never bend further than this

  /* PHONES GET ONE TILE PER ROW, whatever the arithmetic says.
     The maths is width-continuous and has no idea what a phone is, so the
     count fell out of a coincidence: at a 430px viewport (iPhone Pro Max) the
     grid is 398px, two-up gives (398-24)/2 = 187.0px, and the tolerance floor
     is 220 * 0.85 = 187.0px. A dead tie, so it took two — while a 375px phone
     landed at 159px, missed the floor, and got the single column that looks
     right. So the layout changed character between two phones for no reason a
     shopper could perceive, and the big phone got the worse result.

     A collection tile is a browsing decision carrying a 2x2 mosaic and a
     title; at 187px those thumbnails are ~80px each. On a phone it should
     have the width. 600px matches the phone breakpoint the controls use
     (adg-controls.css), so "phone" means one thing across the theme.

     Deliberately keyed to the VIEWPORT, not the grid width: grid width varies
     with each template's gutters (398px here, ~390px on a collection page),
     which would put the switch at a different phone on different pages —
     exactly the inconsistency being removed. Above 600px nothing changes. */
  var PHONE_MAX_W = 600;

  function px(v, fallback) {
    var n = parseFloat(v);
    return isNaN(n) ? fallback : n;
  }

  function choose(width, gap, floor, maxCols, count, ceiling) {
    if (count <= 1) return 1;

    // Largest n whose card still clears the floor (with a little give).
    var best = 1;
    for (var n = Math.min(maxCols, count); n >= 1; n--) {
      if ((width - (n - 1) * gap) / n >= floor * TOLERANCE) { best = n; break; }
    }

    // THE CEILING — the other half of "min and max before the design
    // adapts" (Justin, 08-05). Without it a short group on a wide screen
    // meant two giants side by side. While the cards would still exceed
    // the merchant's max size, take MORE columns instead, as long as they
    // keep clearing the floor. Groups with fewer tiles than that stop at
    // one-per-tile and the CSS max-width cap stops the ballooning.
    if (ceiling > 0) {
      while (best < Math.min(maxCols, count)) {
        var wNow = (width - (best - 1) * gap) / best;
        if (wNow <= ceiling) break;
        var wNext = (width - best * gap) / (best + 1);
        if (wNext < floor * TOLERANCE) break;
        best++;
      }
    }

    // A remainder of exactly 1 is the ugly case — one card alone on the last
    // row, either beside a hole or stretched to a giant. Prefer a slightly
    // tighter count that divides cleanly, as long as cards stay above the
    // hard minimum. Checked for EVERY count, not just the first fit.
    if (best > 1 && count % best === 1) {
      for (var m = best + 1; m <= Math.min(maxCols, count); m++) {
        var w = (width - (m - 1) * gap) / m;
        if (w < floor * HARD_MIN) break;          // too small — stop looking
        if (count % m !== 1) { best = m; break; }
      }
    }
    // Still orphaned? Step DOWN instead — 5 cards at 4-up is 4+1, but at
    // 3-up it is 3+2, which fills both rows with bigger cards.
    if (best > 2 && count % best === 1) {
      for (var k = best - 1; k >= 2; k--) {
        if (count % k !== 1) { best = k; break; }
      }
    }
    return best;
  }

  function layout(grid) {
    var cs = getComputedStyle(grid);
    var width = grid.clientWidth;
    if (!width) return;
    var gap = px(cs.columnGap || cs.gap, 20);
    var floor = px(cs.getPropertyValue('--adg-cat-min'), 220);
    var ceiling = px(cs.getPropertyValue('--adg-cat-max'), 0);
    var maxCols = px(cs.getPropertyValue('--cols-desktop'), 5);
    // Count SLOTS, not cards — a featured card occupies two. Everything else
    // (even division, orphan avoidance, the legibility floor) then works
    // unchanged, which is why emphasis doesn't fight the responsive layout.
    var kids = [].slice.call(grid.children);
    var count = 0;
    kids.forEach(function (c) { count += c.classList.contains('adg-cat--featured') ? 2 : 1; });
    // Clamped AFTER choose() rather than inside it, so choose() stays a pure
    // function of the numbers it is handed and remains testable on its own.
    var n = choose(width, gap, floor, maxCols, count, ceiling);
    if (window.innerWidth <= PHONE_MAX_W) n = 1;
    if (grid.dataset.cols !== String(n)) {
      grid.dataset.cols = String(n);
      grid.style.setProperty('--adg-cat-cols', n);
    }
    // Tell each card how many slots it ACTUALLY ended up spanning. A featured
    // card spans 2; so does a lone card that grew to fill its row. Either way
    // it needs the wide treatment — fewer, bigger thumbnails — instead of a
    // stretched 2x2. The tile script listens for this and re-renders.
    var unit = (width - (n - 1) * gap) / n;
    var changed = false;
    kids.forEach(function (c) {
      var span = Math.max(1, Math.round((c.getBoundingClientRect().width + gap) / (unit + gap)));
      if (c.dataset.span !== String(span)) { c.dataset.span = String(span); changed = true; }
    });
    if (changed) grid.dispatchEvent(new CustomEvent('adg:span', { bubbles: true }));
  }

  var grids = [].slice.call(document.querySelectorAll('.adg-cats__grid'));

  /* The theme editor replaces a section's HTML in place and fires
     shopify:section:load. Without this the NEW grid never gets measured, so
     it has no data-span, and the tile script (which reads span to decide how
     many thumbnails to draw) renders against stale geometry. Adopt the new
     grids and lay them out. */
  document.addEventListener('shopify:section:load', function (e) {
    var host = e.target;
    var fresh = [].slice.call(host.querySelectorAll ? host.querySelectorAll('.adg-cats__grid') : []);
    if (host.matches && host.matches('.adg-cats__grid')) fresh.push(host);
    grids = grids.filter(function (g) { return g.isConnected !== false; }).concat(fresh);
    fresh.forEach(function (g) {
      layout(g);
      if (typeof ro !== 'undefined' && ro) ro.observe(g);
    });
  });

  if (!grids.length) return;
  grids.forEach(layout);

  /* The phone clamp is a VIEWPORT rule living inside a function that otherwise
     only re-runs when a GRID's own box changes. Those usually coincide, but
     not always — a phone rotating 430->932 crosses the boundary, and if a
     grid's box happened not to change with it we would keep the phone's single
     column on a wide screen. Watch the boundary itself, and only re-lay-out on
     the flip, so this costs nothing during an ordinary drag-resize. */
  var wasPhone = window.innerWidth <= PHONE_MAX_W;
  window.addEventListener('resize', function () {
    var isPhone = window.innerWidth <= PHONE_MAX_W;
    if (isPhone !== wasPhone) { wasPhone = isPhone; grids.forEach(layout); }
  }, { passive: true });

  if ('ResizeObserver' in window) {
    var ro = new ResizeObserver(function (entries) {
      entries.forEach(function (e) { layout(e.target); });
    });
    grids.forEach(function (g) { ro.observe(g); });
  } else {
    var t;
    window.addEventListener('resize', function () {
      clearTimeout(t);
      t = setTimeout(function () { grids.forEach(layout); }, 120);
    }, { passive: true });
  }
})();
